import {
  TrackingProblemReport,
  TrackingProblemReportRequestBody,
  TrackingRequestBody,
  TrackingSessionSummary,
  TrackingTechnicalSnapshot,
  TrackingTechnicalSnapshotRequestBody,
} from '../../types/Tracking/types';
import { debugTrackingLog } from '../../utils/tracking';
import { apiRequest } from './apiClient';
import {
  TRACKING_REPORT_PROBLEM,
  TRACKING_SESSION_SUMMARY,
  USER_TRACKING,
} from './constants';

export type TrackingTechnicalSnapshotSyncResult = 'success' | 'retryable' | 'unsupported';
export type TrackingProblemReportSyncResult = 'success' | 'retryable' | 'unsupported';

const isConfirmedProblemReportResponse = (data: unknown): boolean => {
  if (typeof data === 'boolean') return data;
  if (!data || typeof data !== 'object') return true;

  const candidate = data as Record<string, unknown>;

  if (typeof candidate.success === 'boolean') return candidate.success;
  if (typeof candidate.ok === 'boolean') return candidate.ok;
  if (typeof candidate.sent === 'boolean') return candidate.sent;
  if (typeof candidate.messageSent === 'boolean') return candidate.messageSent;

  return true;
};

export const postTrackingSessionSummary = async (
  summary: TrackingSessionSummary,
): Promise<boolean> => {
  const payload: TrackingRequestBody = {
    data: summary,
  };

  debugTrackingLog('Enviando tracking al backend', {
    endpoint: USER_TRACKING,
    payload,
  });

  try {
    const { success, data, error } = await apiRequest(
      'post',
      USER_TRACKING,
      payload,
    );

    debugTrackingLog('Respuesta de tracking del backend', {
      endpoint: USER_TRACKING,
      success,
      data,
      error,
      sessionId: summary.sessionId,
    });

    if (!success) {
      console.error('[postTrackingSessionSummary] failed to submit summary:', error);
    }

    return success;
  } catch (error) {
    console.error('[postTrackingSessionSummary] unexpected error submitting summary:', error);
    debugTrackingLog('Error enviando tracking al backend', {
      endpoint: USER_TRACKING,
      sessionId: summary.sessionId,
      error,
    });
    return false;
  }
};

export const postTrackingTechnicalSnapshot = async (
  snapshot: TrackingTechnicalSnapshot,
): Promise<TrackingTechnicalSnapshotSyncResult> => {
  const payload: TrackingTechnicalSnapshotRequestBody = {
    data: snapshot,
  };

  debugTrackingLog('Enviando snapshot tecnico al backend', {
    endpoint: TRACKING_SESSION_SUMMARY,
    payload,
    sessionId: snapshot.sessionId,
    revision: snapshot.revision,
    phase: snapshot.phase,
  });

  try {
    const { success, data, error } = await apiRequest(
      'post',
      TRACKING_SESSION_SUMMARY,
      payload,
    );

    debugTrackingLog('Respuesta de snapshot tecnico del backend', {
      endpoint: TRACKING_SESSION_SUMMARY,
      success,
      data,
      error,
      sessionId: snapshot.sessionId,
      revision: snapshot.revision,
      phase: snapshot.phase,
    });

    return success ? 'success' : 'retryable';
  } catch (error: any) {
    const status = error?.response?.status ?? error?.apiResponse?.status ?? null;

    debugTrackingLog('Error enviando snapshot tecnico al backend', {
      endpoint: TRACKING_SESSION_SUMMARY,
      sessionId: snapshot.sessionId,
      revision: snapshot.revision,
      phase: snapshot.phase,
      status,
      error,
    });

    if (status === 404 || status === 405 || status === 501) {
      return 'unsupported';
    }

    return 'retryable';
  }
};

export const postTrackingProblemReport = async (
  report: TrackingProblemReport,
): Promise<TrackingProblemReportSyncResult> => {
  const payload: TrackingProblemReportRequestBody = {
    ...report,
    data: report,
  };

  debugTrackingLog('Enviando reporte manual de problema al backend', {
    endpoint: TRACKING_REPORT_PROBLEM,
    payload,
    sessionId: report.sessionId,
    revision: report.revision,
    category: report.category,
  });

  try {
    const { success, data, error } = await apiRequest(
      'post',
      TRACKING_REPORT_PROBLEM,
      payload,
    );

    debugTrackingLog('Respuesta de reporte manual de problema', {
      endpoint: TRACKING_REPORT_PROBLEM,
      success,
      data,
      error,
      sessionId: report.sessionId,
      revision: report.revision,
      category: report.category,
    });

    return success && isConfirmedProblemReportResponse(data) ? 'success' : 'retryable';
  } catch (error: any) {
    const status = error?.response?.status ?? error?.apiResponse?.status ?? null;

    debugTrackingLog('Error enviando reporte manual de problema', {
      endpoint: TRACKING_REPORT_PROBLEM,
      sessionId: report.sessionId,
      revision: report.revision,
      category: report.category,
      status,
      error,
    });

    if (status === 404 || status === 405 || status === 501) {
      return 'unsupported';
    }

    return 'retryable';
  }
};
